import React, { useEffect, useMemo, useState } from "react";
import { NotebookPen, Sparkles, Trash2, Loader2, Phone, Users, CalendarClock, StickyNote, Plus, Save } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  fetchClients, fetchNotes, createNote, deleteNote, summarizeNotes, formatDate,
} from "../../lib/api";
import { VoiceRecorder } from "../common/VoiceRecorder";
import { ClickToCall } from "../common/ClickToCall";
import SummaryView from "../common/SummaryView";

const TYPE_META = {
  call: { label: "Call", icon: Phone, cls: "bg-blue-100 text-blue-700" },
  meeting: { label: "Meeting", icon: Users, cls: "bg-green-100 text-green-700" },
  "follow-up": { label: "Follow-up", icon: CalendarClock, cls: "bg-amber-100 text-amber-700" },
  general: { label: "General", icon: StickyNote, cls: "bg-gray-100 text-gray-600" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ title: "", type: "call", date: todayISO(), content: "" });

export default function Notes() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState({ open: false, text: "", title: "" });
  const [savingSummary, setSavingSummary] = useState(false);

  const client = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);

  useEffect(() => {
    fetchClients().then(setClients).catch(() => {});
  }, []);

  const loadNotes = (cid) => {
    if (!cid) { setNotes([]); return; }
    setLoading(true);
    fetchNotes(token, cid).then(setNotes).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadNotes(clientId); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const appendTranscript = (text) =>
    setForm((f) => ({ ...f, content: f.content ? `${f.content} ${text}` : text }));

  const save = async () => {
    if (!clientId) { toast.error("Pick a client first."); return; }
    if (!form.content.trim()) { toast.error("Write or dictate a note first."); return; }
    setSaving(true);
    try {
      await createNote(token, {
        client_id: clientId,
        client_name: client?.name || "",
        title: form.title.trim() || `${TYPE_META[form.type].label} note`,
        type: form.type,
        date: form.date,
        content: form.content.trim(),
      });
      setForm(emptyForm());
      loadNotes(clientId);
      toast.success("Note saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteNote(token, id);
      setNotes((n) => n.filter((x) => x.id !== id));
      toast.success("Note deleted");
    } catch (e) {
      toast.error("Could not delete note");
    }
  };

  const saveSummary = async () => {
    if (!clientId) { toast.error("Pick a client first to save this summary."); return; }
    if (!summary.text.trim()) return;
    setSavingSummary(true);
    try {
      await createNote(token, {
        client_id: clientId,
        client_name: client?.name || "",
        title: summary.title || "AI Summary",
        type: "summary",
        date: new Date().toISOString().slice(0, 10),
        content: summary.text.trim(),
      });
      loadNotes(clientId);
      toast.success("Summary saved as a note");
      setSummary((s) => ({ ...s, open: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save summary");
    } finally {
      setSavingSummary(false);
    }
  };

  const summarizeCurrent = async () => {
    if (!form.content.trim()) { toast.error("Nothing to summarize yet."); return; }
    setSummarizing(true);
    try {
      const res = await summarizeNotes(token, { mode: "current", text: form.content });
      setSummary({ open: true, text: res.summary, title: "Note Summary" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not summarize");
    } finally {
      setSummarizing(false);
    }
  };

  const summarizeClient = async () => {
    if (!clientId || notes.length === 0) { toast.error("No saved notes for this client yet."); return; }
    setSummarizing(true);
    try {
      const res = await summarizeNotes(token, { mode: "client", client_id: clientId });
      setSummary({ open: true, text: res.summary, title: `Summary · ${client?.name}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not summarize");
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy flex items-center gap-2">
            <NotebookPen className="h-6 w-6 text-gold-dark" /> Client Notes
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Type or speak notes, then let AI summarize them.</p>
        </div>
        <div className="w-full sm:w-72">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="bg-white" data-testid="notes-client-select">
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              {clients.length === 0 && <div className="px-2 py-1.5 text-sm text-gray-400">No clients yet</div>}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!clientId ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center text-gray-400" data-testid="notes-empty-state">
          <NotebookPen className="h-10 w-10 mx-auto mb-3" />
          Select a client above to view and add notes.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Composer */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3" data-testid="note-composer">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Budget review" data-testid="note-title-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="note-date-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500">Note</label>
                <div className="flex items-center gap-2">
                  <ClickToCall token={token} toNumber={client?.phone} clientId={clientId} clientName={client?.name} onTranscript={appendTranscript} />
                  <VoiceRecorder token={token} onText={appendTranscript} label="Speak" />
                </div>
              </div>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={7}
                placeholder="Type your note, or tap Speak to dictate…"
                data-testid="note-content-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white flex-1" data-testid="note-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Save Note</>}
              </Button>
              <Button onClick={summarizeCurrent} disabled={summarizing} variant="outline" className="border-gold text-gold-dark hover:bg-gold/10" data-testid="note-summarize-current">
                {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> AI Summarize</>}
              </Button>
            </div>
          </div>

          {/* Notes list */}
          <div className="space-y-3" data-testid="notes-list">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-lg font-semibold text-navy">{notes.length} note(s)</h2>
              <Button onClick={summarizeClient} disabled={summarizing || notes.length === 0} size="sm" className="bg-gold hover:bg-gold-dark text-navy font-semibold" data-testid="summarize-client-button">
                {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Summarize all</>}
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
            ) : notes.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center text-gray-400">
                No notes yet for {client?.name}.
              </div>
            ) : (
              notes.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.general;
                const Icon = meta.icon;
                return (
                  <div key={n.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`note-${n.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-navy truncate">{n.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(n.date)}</span>
                        </div>
                      </div>
                      <button onClick={() => remove(n.id)} className="text-gray-300 hover:text-red-500 p-1" data-testid={`note-delete-${n.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-2.5 text-sm text-gray-700 whitespace-pre-wrap">{n.content}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <Dialog open={summary.open} onOpenChange={(o) => setSummary((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-lg" data-testid="summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold-dark" /> {summary.title}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-cream rounded-lg p-4 max-h-[60vh] overflow-y-auto">
            <SummaryView text={summary.text} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setSummary((s) => ({ ...s, open: false }))}
              data-testid="summary-close-button"
            >
              Close
            </Button>
            <Button
              onClick={saveSummary}
              disabled={savingSummary || !clientId}
              className="bg-gold hover:bg-gold-dark text-navy font-semibold"
              data-testid="save-summary-button"
            >
              {savingSummary ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save Summary</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
